import { useState } from 'react';
import { cn } from '../../lib/cn';

type Listing = {
  title: string;
  bullets: string[];
  description: string;
  backendKeywords: string;
};

type Props = {
  listing?: Listing;
  fields?: string[];
};

const FIELD_TABS = ['title', 'bullets', 'description', 'keywords'] as const;

export function ListingBuilderPanel({ listing, fields = [] }: Props) {
  const [active, setActive] = useState<(typeof FIELD_TABS)[number]>('title');

  if (!listing) {
    return <p className="text-sm text-slate-500">No listing loaded</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Listing fields">
        {FIELD_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={active === tab}
            onClick={() => setActive(tab)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition',
              active === tab
                ? 'bg-brand-600 text-white'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400',
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {active === 'title' && (
        <div className="rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-700">
          <p className="text-xs text-slate-500">{listing.title.length}/200 chars</p>
          <p className="mt-1 font-medium">{listing.title}</p>
        </div>
      )}

      {active === 'bullets' && (
        <ul className="space-y-2">
          {listing.bullets.map((b, i) => (
            <li key={i} className="rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-700">
              <span className="text-xs text-slate-400">Bullet {i + 1}</span>
              <p className="mt-0.5">{b}</p>
            </li>
          ))}
        </ul>
      )}

      {active === 'description' && (
        <div className="rounded-lg border border-slate-200 p-3 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-400">
          {listing.description}
        </div>
      )}

      {active === 'keywords' && (
        <div className="rounded-lg border border-slate-200 p-3 text-sm font-mono dark:border-slate-700">
          {listing.backendKeywords}
        </div>
      )}

      {fields.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {fields.map((f) => (
            <span key={f} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}
